import { createConsumer } from '@rails/actioncable';
import { emitter } from 'shared/helpers/mitt';
import logger from 'shared/helpers/logger';

// Helper to access the global i18n instance created in dashboard.js entrypoint
// (window.__APP_I18N__ — o nome antigo window.appI18n nunca existiu, então
// TODOS os toasts realtime renderizavam a chave literal; achado QA ciclo 11).
const t = (key, params) => {
  // eslint-disable-next-line no-underscore-dangle
  const i18n = window.__APP_I18N__;
  if (!i18n) return key;
  return i18n.global.t(key, params);
};

// Desconexão sustentada: avisar o usuário após este intervalo sem reconectar
// (~o tempo do backoff completo de 5 tentativas do scheduleReconnect).
const DISCONNECT_NOTIFY_AFTER_MS = 35000;

// Janela de agregação dos toasts de "card criado" (import em massa).
const CREATED_TOAST_WINDOW_MS = 2000;

// Bus events for pipeline real-time updates
export const PIPELINE_EVENTS = {
  CARD_CREATED: 'pipeline_card.created',
  CARD_UPDATED: 'pipeline_card.updated',
  CARD_DELETED: 'pipeline_card.deleted',
  CARD_STAGE_CHANGED: 'pipeline_card.stage_changed',
  CARD_WON: 'pipeline_card.won',
  CARD_LOST: 'pipeline_card.lost',
  CARD_OWNER_CHANGED: 'pipeline_card.owner_changed',
  CARD_REORDERED: 'pipeline_card.reordered',
  PIPELINE_CREATED: 'pipeline.created',
  PIPELINE_UPDATED: 'pipeline.updated',
  PIPELINE_DELETED: 'pipeline.deleted',
  CONNECTION_CHANGED: 'pipeline_cable.connection_changed',
};

class PipelineCardCable {
  constructor() {
    this.consumer = null;
    this.subscription = null;
    this.store = null;
    this.currentPipelineId = null;
    this.currentAccountId = null;
    this.currentUserId = null;
    this.pubsubToken = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
  }

  init(store, pubsubToken) {
    if (this.consumer) {
      // Already initialized
      return this;
    }

    this.store = store;
    this.pubsubToken = pubsubToken;
    this.currentAccountId = store.getters.getCurrentAccountId;
    this.currentUserId = store.getters.getCurrentUserID;

    const { websocketURL = '' } = window.chatwootConfig || {};
    const wsUrl = websocketURL ? `${websocketURL}/cable` : undefined;

    this.consumer = createConsumer(wsUrl);

    return this;
  }

  subscribe(pipelineId) {
    if (!this.consumer || !this.store) {
      logger.warn('[PipelineCardCable] Not initialized. Call init() first.');
      return;
    }

    // Unsubscribe from previous pipeline
    if (this.subscription) {
      this.unsubscribe();
    }

    this.currentPipelineId = pipelineId;

    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'PipelineCardChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
        pipeline_id: pipelineId,
      },
      {
        connected: () => this.onConnected(),
        disconnected: () => this.onDisconnected(),
        received: data => this.onReceived(data),
        rejected: () => this.onRejected(),
      }
    );

    // Assinando sem conexão viva (reconexão em andamento ou primeira conexão
    // que não sobe): garante o aviso de desconexão sustentada armado — o
    // guard interno evita duplicação e onConnected limpa.
    if (!this.isConnected) {
      this.startDisconnectNotifyTimer();
    }
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
      this.currentPipelineId = null;
    }
    this.clearReconnectTimer();
    this.clearResyncTimer();
    // NÃO limpar o disconnectNotifyTimer aqui: o loop de reconexão re-assina
    // via subscribe() → unsubscribe() ~1s após a queda e matava o aviso de
    // 35s (re-QA ciclo 11). O timer só é limpo ao CONECTAR (onConnected) —
    // e o callback dele já é no-op quando isConnected.
  }

  changePipeline(pipelineId) {
    if (!this.subscription) {
      this.subscribe(pipelineId);
      return;
    }

    if (this.currentPipelineId === pipelineId) {
      return;
    }

    // Use the channel's change_pipeline action
    this.subscription.perform('change_pipeline', { pipeline_id: pipelineId });
    this.currentPipelineId = pipelineId;
  }

  disconnect() {
    this.unsubscribe();
    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }
    this.store = null;
    this.isConnected = false;
    // Teardown completo (saída da área do pipeline): aqui sim o aviso de
    // desconexão deixa de fazer sentido — e um toast agregado pendente não
    // pode disparar em outra tela.
    this.clearDisconnectNotifyTimer();
    this.clearCreatedToastTimer();
  }

  // Connection event handlers
  onConnected() {
    // Reconexão (cobre tanto o nosso scheduleReconnect quanto o monitor
    // interno do ActionCable): eventos broadcast durante o gap foram
    // PERDIDOS — sem resync o board fica stale em silêncio.
    const wasReconnection = this.wasDisconnected === true;
    this.wasDisconnected = false;
    this.maxAttemptsNotified = false;
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.clearDisconnectNotifyTimer();

    emitter.emit(PIPELINE_EVENTS.CONNECTION_CHANGED, { connected: true });

    logger.log(
      '[PipelineCardCable] Connected to pipeline:',
      this.currentPipelineId
    );

    if (wasReconnection) {
      // Jitter 0-5s (achado Codex): após restart do servidor, todos os
      // clientes reconectam juntos — sem espalhar, o refetch simultâneo de
      // N boards vira thundering herd no Puma.
      const jitter = Math.floor(Math.random() * 5000);
      logger.log(
        `[PipelineCardCable] Reconnected — resyncing board state in ${jitter}ms`
      );
      this.clearResyncTimer();
      this.resyncTimer = setTimeout(() => this.resyncAfterReconnect(), jitter);
    }
  }

  clearResyncTimer() {
    if (this.resyncTimer) {
      clearTimeout(this.resyncTimer);
      this.resyncTimer = null;
    }
  }

  // Toast único quando a desconexão persiste por DISCONNECT_NOTIFY_AFTER_MS
  // — independe de quantos eventos 'disconnected' o ActionCable repassa.
  startDisconnectNotifyTimer() {
    if (this.disconnectNotifyTimer || this.maxAttemptsNotified) return;
    this.disconnectNotifyTimer = setTimeout(() => {
      this.disconnectNotifyTimer = null;
      if (!this.isConnected && !this.maxAttemptsNotified) {
        this.maxAttemptsNotified = true;
        this.showNotification(
          t('PIPELINE.NOTIFICATIONS.REALTIME_DISCONNECTED')
        );
      }
    }, DISCONNECT_NOTIFY_AFTER_MS);
  }

  clearDisconnectNotifyTimer() {
    if (this.disconnectNotifyTimer) {
      clearTimeout(this.disconnectNotifyTimer);
      this.disconnectNotifyTimer = null;
    }
  }

  // Evento de controle do backend: a lista de agents do pipeline mudou.
  // Re-assina — o channel re-avalia a visibilidade e REJEITA quem foi
  // removido (revogação efetiva do stream; sem isso o agente removido
  // seguia recebendo eventos até recarregar a página).
  handleMembershipChanged(data) {
    if (!this.isForCurrentPipeline(data.pipeline_id)) return;
    logger.log(
      '[PipelineCardCable] Pipeline membership changed — resubscribing'
    );
    this.subscribe(this.currentPipelineId);
  }

  // Refetch coerente com a view ativa: invalida o cache (senão o TTL de 5min
  // devolveria o estado stale) e refaz pelo MESMO modo do último load.
  async resyncAfterReconnect() {
    if (!this.store) return;
    try {
      const mode = this.store.state.pipelineCards?.lastFetchedMode;
      await this.store.dispatch('pipelineCards/resetCache');
      if (mode === 'global') {
        await this.store.dispatch('pipelineCards/fetchPipelineCardItems');
      } else {
        await this.store.dispatch('pipelineCards/fetchBoardByStages');
      }
    } catch (error) {
      logger.error('[PipelineCardCable] Resync after reconnect failed', error);
    }
  }

  onDisconnected() {
    this.isConnected = false;
    this.wasDisconnected = true;
    this.clearResyncTimer();
    // Aviso de desconexão por TEMPO, não por contagem de tentativas: o
    // ActionCable emite 'disconnected' UMA vez por queda (as ~26 falhas de
    // reconexão internas não repassam o evento), então o contador nunca
    // atinge o máximo em offline sustentado (achado QA ciclo 11).
    this.startDisconnectNotifyTimer();

    emitter.emit(PIPELINE_EVENTS.CONNECTION_CHANGED, { connected: false });

    logger.log('[PipelineCardCable] Disconnected');

    // Attempt to reconnect
    this.scheduleReconnect();
  }

  onRejected() {
    logger.warn(
      '[PipelineCardCable] Subscription rejected - check authentication'
    );
    this.isConnected = false;
  }

  // Reconnection logic
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.warn('[PipelineCardCable] Max reconnect attempts reached');
      // Avisa o usuário UMA vez — antes, o board ficava permanentemente
      // stale em silêncio após ~31s de backoff esgotado.
      if (!this.maxAttemptsNotified) {
        this.maxAttemptsNotified = true;
        this.showNotification(
          t('PIPELINE.NOTIFICATIONS.REALTIME_DISCONNECTED')
        );
      }
      return;
    }

    this.clearReconnectTimer();

    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts += 1;
      if (this.currentPipelineId) {
        this.subscribe(this.currentPipelineId);
      }
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Message handler
  onReceived(data) {
    if (!data || !data.event) {
      return;
    }

    const { event, triggered_by: triggeredBy } = data;

    // Ignore events triggered by the current user (their optimistic update
    // already applied). Coerce both sides: user_id may arrive as a number
    // while the store getter returns a string (or vice-versa); a strict
    // `===` mismatch would fail to suppress the echo and re-apply it.
    if (
      triggeredBy?.user_id != null &&
      Number(triggeredBy.user_id) === Number(this.currentUserId)
    ) {
      return;
    }

    // Validate account (defense in depth — backend now includes account_id).
    if (
      data.account_id != null &&
      Number(data.account_id) !== Number(this.currentAccountId)
    ) {
      return;
    }

    // Route to appropriate handler
    const handlers = {
      'pipeline_card.created': () => this.handleCardCreated(data),
      'pipeline_card.updated': () => this.handleCardUpdated(data),
      'pipeline_card.deleted': () => this.handleCardDeleted(data),
      'pipeline_card.stage_changed': () => this.handleStageChanged(data),
      'pipeline_card.won': () => this.handleCardWon(data),
      'pipeline_card.lost': () => this.handleCardLost(data),
      'pipeline_card.owner_changed': () => this.handleOwnerChanged(data),
      'pipeline_card.reordered': () => this.handleCardsReordered(data),
      'pipeline.membership_changed': () => this.handleMembershipChanged(data),
    };

    const handler = handlers[event];
    if (handler) {
      handler();
    }
  }

  // Whether an event scoped to `pipelineId` belongs to the pipeline the
  // user is currently viewing. With no pipeline selected, or when the
  // payload carries no pipeline info, accept it (never wrongly drop).
  isForCurrentPipeline(pipelineId) {
    if (!this.currentPipelineId) return true;
    if (pipelineId == null) return true;
    return Number(pipelineId) === Number(this.currentPipelineId);
  }

  // Card event handlers
  handleCardCreated(data) {
    const { card } = data;
    if (!card) return;

    // B54: ignore events for pipelines other than the currently viewed one
    if (!this.isForCurrentPipeline(card.pipeline_id)) return;

    this.store.commit('pipelineCards/ADD_PIPELINE_CARD_REMOTE', card);
    emitter.emit(PIPELINE_EVENTS.CARD_CREATED, data);

    this.queueCreatedToast(
      data.triggered_by?.user_name || t('PIPELINE.NOTIFICATIONS.ANOTHER_USER')
    );
  }

  // Agrupa toasts de criação numa janela curta: um import CSV de 300 cards
  // gerava 300 broadcasts → 300 toasts em quem estivesse com o board aberto
  // (re-auditoria rodada 2, achado #7). 1 card = toast normal; N de UM autor
  // = agregado com o nome; N de VÁRIOS autores = agregado genérico (last-wins
  // atribuiria tudo ao último — achado Codex).
  queueCreatedToast(userName) {
    this.createdToastCount = (this.createdToastCount || 0) + 1;
    this.createdToastUsers = this.createdToastUsers || new Set();
    this.createdToastUsers.add(userName);
    if (this.createdToastTimer) return;

    this.createdToastTimer = setTimeout(() => {
      const count = this.createdToastCount;
      const users = [...(this.createdToastUsers || [])];
      this.clearCreatedToastTimer();

      let message;
      if (count <= 1) {
        message = t('PIPELINE.NOTIFICATIONS.CARD_CREATED', { user: users[0] });
      } else if (users.length === 1) {
        message = t('PIPELINE.NOTIFICATIONS.CARDS_CREATED_BULK', {
          count,
          user: users[0],
        });
      } else {
        message = t('PIPELINE.NOTIFICATIONS.CARDS_CREATED_MULTI', { count });
      }
      this.showNotification(message);
    }, CREATED_TOAST_WINDOW_MS);
  }

  clearCreatedToastTimer() {
    if (this.createdToastTimer) {
      clearTimeout(this.createdToastTimer);
    }
    this.createdToastTimer = null;
    this.createdToastCount = 0;
    this.createdToastUsers = null;
  }

  handleCardUpdated(data) {
    const { card, changed_fields: changedFields } = data;
    if (!card) return;

    // B54: ignore events for pipelines other than the currently viewed one
    if (!this.isForCurrentPipeline(card.pipeline_id)) return;

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card,
      changedFields,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_UPDATED, data);
  }

  handleCardDeleted(data) {
    const { card } = data;
    const cardId = card?.id;
    if (!cardId) return;

    // B54: ignore events for pipelines other than the currently viewed one
    if (!this.isForCurrentPipeline(card.pipeline_id)) return;

    this.store.commit('pipelineCards/REMOVE_PIPELINE_CARD_REMOTE', cardId);
    emitter.emit(PIPELINE_EVENTS.CARD_DELETED, data);

    this.showNotification(
      t('PIPELINE.NOTIFICATIONS.CARD_DELETED', {
        user:
          data.triggered_by?.user_name ||
          t('PIPELINE.NOTIFICATIONS.ANOTHER_USER'),
      })
    );
  }

  handleStageChanged(data) {
    const { card, from_stage: fromStage, to_stage: toStage } = data;
    if (!card) return;

    // Ignore events for pipelines other than the currently viewed one.
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_CARD_STAGE_REMOTE', {
      cardId: card.id,
      stage: card.stage,
      position: card.position,
      fromStage,
      toStage,
      version: card.updated_at,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_STAGE_CHANGED, data);
  }

  handleCardWon(data) {
    const { card } = data;
    if (!card) return;
    // Guard de pipeline (B54) — sem ele, vitórias em QUALQUER pipeline da
    // conta disparavam toast/commit enquanto você olha outro board.
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card: { ...card, won_at: card.won_at },
    });
    emitter.emit(PIPELINE_EVENTS.CARD_WON, data);

    this.showNotification(
      t('PIPELINE.NOTIFICATIONS.CARD_WON', {
        user:
          data.triggered_by?.user_name ||
          t('PIPELINE.NOTIFICATIONS.ANOTHER_USER'),
      })
    );
  }

  handleCardLost(data) {
    const { card, lost_reason: lostReason } = data;
    if (!card) return;
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card: { ...card, lost_at: card.lost_at, lost_reason: lostReason },
    });
    emitter.emit(PIPELINE_EVENTS.CARD_LOST, data);
  }

  handleOwnerChanged(data) {
    const { card, previous_owner_id: previousOwnerId } = data;
    if (!card) return;

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card: { ...card, owner_id: card.owner_id },
      previousOwnerId,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_OWNER_CHANGED, data);
  }

  handleCardsReordered(data) {
    const { cards, positions } = data;
    if (!cards || !positions) return;

    // Ignore events for pipelines other than the currently viewed one.
    if (!this.isForCurrentPipeline(data.pipeline_id)) return;

    this.store.commit('pipelineCards/REORDER_CARDS_REMOTE', {
      cards,
      positions,
      version: data.timestamp,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_REORDERED, data);
  }

  // NOTE: pipeline-level events (pipeline.created/updated/deleted) are NOT
  // broadcast by the backend today, so their handlers were removed to avoid
  // dead routing. The `pipelines/*_PIPELINE_REMOTE` mutations remain in the
  // store (used by pipelines.js actions). Re-add handlers here if/when the
  // backend starts broadcasting Pipeline model changes.

  // Utility methods
  // eslint-disable-next-line class-methods-use-this
  showNotification(message) {
    emitter.emit('newToastMessage', { message });
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      currentPipelineId: this.currentPipelineId,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  isInitialized() {
    return Boolean(this.consumer && this.store);
  }
}

// Singleton instance
const pipelineCardCable = new PipelineCardCable();

export default pipelineCardCable;
